import _ from 'lodash'
import Backbone from 'backbone'
import i18n from 'scoreboard/components/i18n'
import template from './report-results.hbs'
import InfiniteScroller from 'scoreboard/components/infinite-scroller'
import scrollContainer from 'scoreboard/components/scroll-container'
import StatefulView from 'scoreboard/reports/common/stateful-view'
import BottomRepeatByHeaderCell from 'scoreboard/reports/common/table/bottom-repeat-by-header-cell'
import streamLoader from 'scoreboard/components/export/stream-loader'
import excelExport from 'scoreboard/components/export/excel-export'
import { streamLoaderPromise }  from 'scoreboard/components/export/stream-loader'
import * as reportWarnings from './report-warnings'

const BaseReportResults = StatefulView.extend({

	className: 'scroll-layout-xy table-hack-row reports-overview report display-pane-body',

	initialize(options = {}) {
		this.options = options

		this.viewModel = this._viewModel()
		//share the stream from the persistence model. This should not be done
		//if multiple views intend to use the stream, but in this scenario only
		//the results view will be needing to access the stream
		this.viewModel.stream(this.model.stream(), {
			// #43860: We have to pass in the results that have already loaded
			// because this call to stream will reassign allData from options, and it
			// will be blank if we don't provide it.
			allData: this.model.initialData
		})

		this.parent = this.options.parent
		this.parent.deferredReady = true

		let TableModel = this._reportTableModel()
		this.tableModel = new TableModel()

		this._setupStream(this.viewModel.stream())
		this.listenTo(this.viewModel, 'stream', stream => {
			this._setupStream(stream)
		})

		//https://github.com/SpiderStrategies/Scoreboard/issues/42604#issuecomment-996178727
		this.viewModel.showSortWarning = this.model.get('dirty') || this.model.isNew()
		this._setupListeners()
	},

	_setupListeners: function () {
		//#46241 listenTo the model so we can stop listening when the view is removed
		this.listenTo(this.model, 'sync', () => {
			this.viewModel.showSortWarning = false
		})

		//#44455 #44729 #44901 #44902 #44903 pass changes to view model
		this.listenTo(this.model, 'change', model => {
			this.viewModel.set(model.attributes, {parse: true})
		})

		//#46117 if the underlying model's stream has changed, update the view model
		//with the new stream
		this.listenTo(this.model, 'stream', modelStream => {
			this.viewModel.stream(modelStream)
		})
	},

	_viewModel: function() {
		//#42604 use a copy of the model that is intended for persistence
		return this.model.clone()
	},

	_setupStream: function(stream) {
		this._stream = stream
		this._stream.on('data', data => {
			_.invoke(this._scroller, 'dataReady')
		})
	},

	_template: function(ctx) {
		return template(ctx)
	},

	render() {
		this._clearComponents()
		let {tableModel} = this
		let model = this.viewModel
		let warnings = model.warnings()
		let dataset = model.get('dataset')

		// Only show the building message for non-dashboards #52446
		if (!this.options.dashboardWidget && dataset?.get('building')) {
			warnings.push(i18n.get('common.report.dataset.rebuilding'))
		}

		this.$el.html(this._template({ warnings }))

		if (model.stacks().length == 0) {
			this.$('.banner-info').toggleClass('is-not-displayed', false)
				.html(i18n.get('report.wizard.noColumns'))
			this.$('.panel-with-table').addClass('is-not-displayed')
		} else {
			let hasCustomWarnings = warnings.length > 0
			model.resultsMetadata().then(meta => {
				reportWarnings.show(this.$el, model, meta, hasCustomWarnings)
			})

			let tableNode = this.$('.table')[0]
			let tableOpts = {
				editable: false,
				disableLoadingDots: this.options.dashboardWidget,
				hideHeaders: this.options.showHeaders === false
			}
			this._table = tableModel.build(model, tableNode, tableOpts)

			//toggle sort states
			this._table.on('select', (cell, nodes) => {
				let { column } = cell
				if (!column || !cell.sortable) { return }

				//when sorting in a breakdown on the results view, force the breakdowns
				//to sort by every value (even if they were set otherwise on the edit)
				//see https://github.com/SpiderStrategies/Scoreboard/issues/26584#issue-503787990
				if (cell instanceof BottomRepeatByHeaderCell) {
					_.each(cell.stack.breakdowns(), breakdown => {
						breakdown.unset('sortByValue')
					})
				}

				let useSortTypeProperty = !column.isGrouping() ||
					column.isAggregatedGroupedColumn(this.model)

				let sortProperty = useSortTypeProperty ? 'sortType' : 'groupingSortType'
				let sortType = column.get(sortProperty)
				let supportsHierarchyOrdering = _.get(this.model
						.columnDataByFieldIdOrColumnType(column.fieldIdOrColumnType()),
					'supportsHierarchyOrdering', false)
				if (!useSortTypeProperty && column.isGrouping() && !supportsHierarchyOrdering) {
					//if we're grouping and the column doesn't support heirarchy
					//then just flip between 2 states, ascending or descending
					column.set(sortProperty, sortType == 'descending' ? 'ascending' : 'descending')
				} else {
					if (_.isNil(sortType)) {
						if (supportsHierarchyOrdering) {
							column.set(sortProperty, 'tree')
						} else {
							column.set(sortProperty, 'ascending')
						}
					} else if (sortType === 'tree') {
						column.set(sortProperty, 'ascending')
					} else if (sortType === 'descending') {
						column.unset(sortProperty)
					} else {
						column.set(sortProperty, 'descending')
					}
				}
			})

			this.setUpScroller()

			//#39389 notify exports when done
			if (this.parent) {
				streamLoader(this.parent, this._stream)
			}
		}

		return this
	},

	setUpScroller: function() {
		//setup the scroll and call to get the next buffered data if any
		let el = this.options.scrollContainer || scrollContainer(this.el)
		this._scroller = new InfiniteScroller({ el })
		this._scroller.on('scroll', () => this._stream.next())
		this._scroller.checkScroll()
	},

	exportTable: function(label) {
		var opts = {
			label: label,
			columnHeadings: this.$('.header-row td'),
			dataAttributes: {
				tr: 'data-id',
				modelLookup: this.exportModelLookup.bind(this)
			}
		}
		return streamLoaderPromise(this, this._stream).then(() => {
			return excelExport.saveTableAsFile(this.$('.table'), opts)
		})
	},

	getState: function() {
		//#45512 return the state of the view model rather than the persisted model
		return this.viewModel.attributes
	},

	/**
	 * The cell's raw data values are stored on jqCell[0].exportModel by
	 * designer-data-extractor.js#_storeExportModel, but data mutations that are only
	 * needed for the excel export should be deferred until the user actually
	 * requests an export and this function is invoked.
	 */
	exportModelLookup(tableId, tableRowIndex, jqCell) {
		if (!_.isEmpty(jqCell) && jqCell[0].exportModel) {
			let {
				value, dataType, precision, currencyCode, timeFormat, color
			} = jqCell[0].exportModel
			return {
				value,
				dataType,
				precision,
				currencyCode,
				timeFormat,
				color
			}
		}
		return null
	},

	_clearComponents() {
		_.invokeMap([this._scroller, this._table], 'remove')
	},

	remove() {
		//#40322 ensure we don't have the cached stream listened on an old view
		this._stream.removeAllListeners('data')
		this._clearComponents()
		this.stopListening(this.model)
		Backbone.View.prototype.remove.apply(this, arguments)
	}
})

export default BaseReportResults
export { template }
BaseReportResults.__filename__ = 'reports/common/base-report-results.js'
